import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, Send } from 'lucide-react';
import { usePublishingStore } from '../../store/publishingStore';
import { ScheduledPost, Campaign, PlatformConnection } from '../../services/publishing.service';
import { toast } from 'sonner';
import { getPlatformIcon, getPlatformName, getPlatformColor } from './components/PlatformConnectionCard';

interface SchedulePostModalProps {
    isOpen: boolean;
    onClose: () => void;
    editingPost: ScheduledPost | null;
    campaigns: Campaign[];
    platforms: PlatformConnection[];
}

const SchedulePostModal: React.FC<SchedulePostModalProps> = ({
    isOpen,
    onClose,
    editingPost,
    campaigns,
    platforms,
}) => {
    const { createPost, updatePost, fetchPosts } = usePublishingStore();

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        platformConnectionId: '',
        campaignId: '',
        scheduledFor: '',
        scheduledTime: '',
        status: 'scheduled',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Connected platforms only
    const connectedPlatforms = platforms.filter(p => p.status === 'connected');

    useEffect(() => {
        if (editingPost) {
            const scheduledDate = new Date(editingPost.scheduledFor);
            setFormData({
                title: editingPost.title,
                description: editingPost.description || '',
                platformConnectionId: typeof editingPost.platformConnectionId === 'string'
                    ? editingPost.platformConnectionId
                    : editingPost.platformConnectionId._id,
                campaignId: editingPost.campaignId
                    ? (typeof editingPost.campaignId === 'string' ? editingPost.campaignId : editingPost.campaignId._id)
                    : '',
                scheduledFor: scheduledDate.toISOString().split('T')[0],
                scheduledTime: scheduledDate.toTimeString().slice(0, 5),
                status: editingPost.status,
            });
        } else {
            // Default to tomorrow at noon
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            tomorrow.setHours(12, 0, 0, 0);

            setFormData({
                title: '',
                description: '',
                platformConnectionId: connectedPlatforms[0]?.id || '',
                campaignId: '',
                scheduledFor: tomorrow.toISOString().split('T')[0],
                scheduledTime: '12:00',
                status: 'scheduled',
            });
        }
    }, [editingPost, connectedPlatforms]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.title.trim()) {
            toast.error('Please enter a title');
            return;
        }
        if (!formData.platformConnectionId) {
            toast.error('Please select a platform');
            return;
        }
        if (!formData.scheduledFor || !formData.scheduledTime) {
            toast.error('Please set a schedule date and time');
            return;
        }

        setIsSubmitting(true);

        try {
            const scheduledDateTime = new Date(`${formData.scheduledFor}T${formData.scheduledTime}`);

            const postData: {
                title: string;
                description?: string;
                platformConnectionId: string;
                campaignId?: string;
                scheduledFor: string;
                status?: 'draft' | 'scheduled' | 'ready' | 'needs_media' | 'publishing' | 'published' | 'failed' | 'cancelled';
            } = {
                title: formData.title.trim(),
                platformConnectionId: formData.platformConnectionId,
                scheduledFor: scheduledDateTime.toISOString(),
                status: formData.status as 'draft' | 'scheduled',
            };

            if (formData.description.trim()) {
                postData.description = formData.description.trim();
            }
            if (formData.campaignId) {
                postData.campaignId = formData.campaignId;
            }

            if (editingPost) {
                await updatePost(editingPost.id, postData as Partial<ScheduledPost>);
            } else {
                await createPost(postData as Parameters<typeof createPost>[0]);
            }

            await fetchPosts();
            onClose();
        } catch (error) {
            console.error('Failed to save post:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-80" onClick={onClose} />

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

                <div className="inline-block w-full max-w-lg p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-3xl">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-indigo-600 rounded-xl flex items-center justify-center">
                                <Send className="w-5 h-5 text-white" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                                {editingPost ? 'Edit Scheduled Post' : 'Schedule New Post'}
                            </h3>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Title */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                Title *
                            </label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                placeholder="Enter post title"
                                className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                Description
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Enter post description (optional)"
                                rows={3}
                                className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 resize-none"
                            />
                        </div>

                        {/* Platform Selection */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                Platform *
                            </label>
                            {connectedPlatforms.length === 0 ? (
                                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl text-sm text-yellow-700 dark:text-yellow-300">
                                    No connected platforms. Please connect a platform first.
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-2">
                                    {connectedPlatforms.map((platform) => (
                                        <button
                                            key={platform.id}
                                            type="button"
                                            onClick={() => setFormData({ ...formData, platformConnectionId: platform.id })}
                                            className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${formData.platformConnectionId === platform.id
                                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                                                }`}
                                        >
                                            <div className={`w-8 h-8 ${getPlatformColor(platform.platform)} rounded-lg flex items-center justify-center text-white`}>
                                                {getPlatformIcon(platform.platform, 'w-4 h-4')}
                                            </div>
                                            <div className="text-left">
                                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {getPlatformName(platform.platform)}
                                                </p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">@{platform.accountName}</p>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Campaign */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                Campaign (Optional)
                            </label>
                            <select
                                value={formData.campaignId}
                                onChange={(e) => setFormData({ ...formData, campaignId: e.target.value })}
                                className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            >
                                <option value="">No Campaign</option>
                                {campaigns.map((campaign) => (
                                    <option key={campaign.id} value={campaign.id}>
                                        {campaign.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Schedule Date & Time */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                    <Calendar className="w-4 h-4 inline mr-1" />
                                    Date *
                                </label>
                                <input
                                    type="date"
                                    value={formData.scheduledFor}
                                    onChange={(e) => setFormData({ ...formData, scheduledFor: e.target.value })}
                                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                    <Clock className="w-4 h-4 inline mr-1" />
                                    Time *
                                </label>
                                <input
                                    type="time"
                                    value={formData.scheduledTime}
                                    onChange={(e) => setFormData({ ...formData, scheduledTime: e.target.value })}
                                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                />
                            </div>
                        </div>

                        {/* Status */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                                Status
                            </label>
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, status: 'draft' })}
                                    className={`flex-1 py-2.5 rounded-xl font-medium transition-colors ${formData.status === 'draft'
                                        ? 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-150 dark:hover:bg-gray-650'
                                        }`}
                                >
                                    Save as Draft
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, status: 'scheduled' })}
                                    className={`flex-1 py-2.5 rounded-xl font-medium transition-colors ${formData.status === 'scheduled'
                                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-150 dark:hover:bg-gray-650'
                                        }`}
                                >
                                    Schedule
                                </button>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 px-4 py-3 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting || connectedPlatforms.length === 0}
                                className="flex-1 px-4 py-3 bg-gradient-to-r from-primary-600 to-indigo-600 text-white rounded-xl hover:from-primary-700 hover:to-indigo-700 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSubmitting ? 'Saving...' : editingPost ? 'Update Post' : 'Schedule Post'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default SchedulePostModal;
